// Single source of truth for bilingual (中文 / English) UI labels.
//
// Mirrors the centralized pattern of getProgramTypeGlyph() in types/program.ts —
// never inline 中文 in components; add an entry here and reference it by key.
//
// English is always the canonical label. The Chinese string is shown *alongside*
// English only on a bilingual brand (see useBilingualEnabled.ts).
// Unlike the decorative SectionGlyph, these labels carry meaning and must NOT be
// aria-hidden.

export type LabelKey =
  | 'nav.dashboard'
  | 'nav.programs'
  | 'nav.students'
  | 'nav.payments'
  | 'nav.account'
  | 'action.enroll'
  | 'action.enrollShort'
  | 'action.addStudent'
  | 'action.pay'
  | 'action.payNow'
  | 'action.checkout'
  | 'action.checkOutPickup'
  | 'action.payOnlyThis'
  | 'action.edit'
  | 'billing.monthly'
  | 'billing.fixedTerm'
  | 'billing.perDay'
  | 'billing.flexMonthly'
  // Payment / invoice status (enum)
  | 'status.paid'
  | 'status.partial'
  | 'status.pending'
  | 'status.overdue'
  | 'status.voided'
  | 'status.processing'
  // Enrollment status (badge)
  | 'status.active'
  | 'status.inactive'
  // Program enrollment lock (badge + parent-facing notice)
  | 'status.enrollmentClosed'
  | 'program.enrollmentClosedNotice'
  // Program capacity and waitlist (BHCS-124)
  | 'status.full'
  | 'program.fullNotice'
  | 'action.joinWaitlist'
  | 'action.leaveWaitlist'
  | 'waitlist.onWaitlist'
  | 'waitlist.seatHeld'
  | 'waitlist.whichChild'
  // More action buttons
  | 'action.unenroll'
  | 'action.inviteCoParent'
  | 'action.inviteShort'
  | 'action.backToDashboard'
  | 'action.viewProgram'
  | 'action.viewInvoice'
  | 'action.addPaymentMethod'
  | 'action.signUpEvents'
  | 'action.backToPayments'
  | 'action.addFunds'
  | 'action.withdraw'
  | 'action.managePaymentMethods'
  | 'action.allPrograms'
  | 'action.savePlan'
  // Flex monthly plan surface
  | 'flex.planTitle'
  | 'flex.planningAhead'
  | 'flex.planAheadDraft'
  | 'flex.pastMonths'
  | 'flex.notAvailable'
  | 'flex.askAssistant'
  | 'flex.clearAll'
  | 'flex.daysAttended'
  | 'flex.thisMonth'
  | 'flex.thisMonthNote'
  | 'flex.nextMonth'
  | 'flex.nextMonthNote'
  | 'flexStatus.draft'
  | 'flexStatus.locked'
  | 'flexStatus.billed'
  | 'flexStatus.skipped'
  // Section headers / field labels
  | 'header.activeEnrollments'
  | 'header.today'
  | 'header.pickup'
  | 'header.nextClass'
  | 'header.needsYou'
  | 'header.enrolled'
  | 'header.thisWeek'
  | 'header.enrollmentHistory'
  | 'header.paymentHistory'
  | 'header.waiver'
  | 'header.emergencyContact'
  | 'header.allergies'
  | 'header.physician'
  | 'header.familyAccess'
  | 'header.paymentMethods'
  | 'header.balanceRewards'
  | 'header.quickActions'
  | 'header.activePrograms'
  | 'header.recommendedPrograms'
  | 'header.totalBalance'
  | 'header.recentActivity'
  | 'header.aboutProgram'
  | 'header.pricing'
  | 'header.pricingDetails'
  | 'header.enrolledStudents'
  | 'header.selectWeeklySessions'
  | 'header.selectDays'
  | 'header.programDetails'
  | 'header.lineItems'
  | 'header.enrollmentDetails'
  | 'header.withdrawFunds'
  | 'header.paymentPreferences'
  | 'header.accountInformation'
  | 'header.paymentSettings'
  | 'header.aboutPortal'
  | 'label.amountDue'
  | 'label.balanceDue'
  | 'label.dueNow'
  | 'label.nextClass'
  | 'label.history'
  | 'label.lunchIncluded'
  | 'label.lunchNotIncluded'
  | 'label.fallbackPaymentMethod'
  | 'label.noClassOn'
  | 'label.programDayOff'
  | 'label.session'
  | 'label.sessions'
  // Tabs
  | 'tab.invoices'
  | 'tab.cash'
  // Footer
  | 'footer.privacyPolicy'
  | 'footer.termsOfService'
  // Account menu
  | 'menu.accountSettings'
  | 'menu.signOut'
  | 'nav.faq'
  | 'nav.assistant'
  | 'nav.bhcsCash'
  | 'nav.meetingSignup'
  | 'nav.autoPay'
  // Parent Genie assistant
  | 'genie.title'
  | 'genie.subtitle'
  | 'genie.placeholder'
  | 'genie.newChat'
  | 'genie.thinking'
  | 'genie.disclaimer'
  | 'genie.feedbackPrompt'
  | 'genie.feedbackThanks'
  | 'genie.stop'
  | 'genie.retry'
  | 'genie.cancelled'
  | 'genie.status.thinking'
  | 'genie.status.querying'
  | 'genie.status.digging'
  | 'genie.close'
  | 'genie.openFullPage'
  | 'genie.chip.explainInvoices'
  | 'genie.chip.signUp'
  | 'genie.chip.flexDays'
  | 'genie.chip.nextClassWhere'
  | 'genie.chip.comingUp'
  | 'genie.chip.programsForAge'
  | 'genie.chip.nextClass';

export interface LabelEntry {
  /** English — the canonical label, always shown. */
  en: string;
  /** Chinese — shown alongside English only on a bilingual brand. */
  zh: string;
}

export const LABELS: Record<LabelKey, LabelEntry> = {
  // Navigation (bottom nav + desktop nav)
  'nav.dashboard':     { en: 'Dashboard', zh: '主页' },
  'nav.programs':      { en: 'Programs',  zh: '课程' },
  'nav.students':      { en: 'Students',  zh: '学生' },
  'nav.payments':      { en: 'Payments',  zh: '账单' }, // "bills" — the page shows history + invoices
  'nav.account':       { en: 'Account',   zh: '账户' },
  // Primary action verbs
  'action.enroll':      { en: 'Enroll a Student', zh: '报名' },
  'action.enrollShort': { en: 'Enroll',           zh: '报名' }, // compact form for the program-card button
  'action.addStudent':  { en: 'Add Student',      zh: '添加学生' },
  'action.pay':         { en: 'Pay',              zh: '缴费' }, // verb prefix on "Pay $X" submit buttons
  'action.payNow':      { en: 'Pay Now',          zh: '缴费' },
  'action.checkout':    { en: 'Checkout',         zh: '结账' }, // bundle payment: pay every ticked invoice at once
  'action.checkOutPickup': { en: 'Check out for pickup', zh: '接孩子签退' }, // parent self check-out, Today card
  'action.payOnlyThis': { en: 'Pay only this',    zh: '单独缴费' }, // bundle payment: one invoice, the usual checkout
  'action.edit':        { en: 'Edit',             zh: '编辑' },
  // Program billing types (badge labels) — en must match getBillingTypeDisplay()
  'billing.monthly':    { en: 'Monthly',      zh: '按月' },
  'billing.fixedTerm':  { en: 'Fixed Term',   zh: '整期' },
  'billing.perDay':     { en: 'Per Day',      zh: '按天' },
  'billing.flexMonthly':{ en: 'Flex Monthly', zh: '按月自选' },
  // Payment / invoice status — en must match getStatusLabel()
  'status.paid':        { en: 'Paid',             zh: '已付清' },
  'status.partial':     { en: 'Partially Paid',   zh: '部分支付' },
  'status.pending':     { en: 'Unpaid',           zh: '待付款' },
  'status.overdue':     { en: 'Overdue',          zh: '逾期' },
  'status.voided':      { en: 'Voided',           zh: '已作废' },
  'status.processing':  { en: 'Payment Processing', zh: '处理中' },
  // Enrollment status badge
  'status.active':      { en: 'Active',           zh: '进行中' },
  'status.inactive':    { en: 'Inactive',         zh: '已结束' },
  // Program enrollment lock — the program is still running, it just isn't taking new students.
  'status.enrollmentClosed':        { en: 'Contact School to Enroll', zh: '暂不支持在线报名' },
  'program.enrollmentClosedNotice': {
    en: "This program isn't open for online enrollment at the moment. Please contact the school.",
    zh: '本课程暂不支持在线报名，如需报名请联系学校。',
  },
  // Program capacity and waitlist. The school, not the system, decides who is offered a seat.
  'status.full':          { en: 'Full',           zh: '已满' },
  'program.fullNotice': {
    en: 'This class is full. Join the waitlist and we will email you if a seat opens for your child.',
    zh: '本课程名额已满。加入候补名单后，如有空位，我们会发邮件通知您。',
  },
  'action.joinWaitlist':  { en: 'Join Waitlist',  zh: '加入候补' },
  'action.leaveWaitlist': { en: 'Leave Waitlist', zh: '退出候补' },
  'waitlist.onWaitlist':  { en: 'On the waitlist', zh: '候补中' },
  'waitlist.seatHeld': {
    en: 'A seat is being held for you. Enroll to claim it.',
    zh: '已为您保留名额，请完成报名。',
  },
  'waitlist.whichChild':  { en: 'Which child?',   zh: '选择孩子' },
  // More action buttons
  'action.unenroll':         { en: 'Unenroll',           zh: '退课' },
  'action.inviteCoParent':   { en: 'Invite a co-parent', zh: '邀请家长' },
  'action.inviteShort':      { en: 'Invite',             zh: '邀请' }, // compact form for the student-card family-access button
  'action.backToDashboard':  { en: 'Back to Dashboard',  zh: '返回主页' },
  'action.viewProgram':      { en: 'View Program',       zh: '查看课程' },
  'action.viewInvoice':      { en: 'View Invoice',       zh: '查看账单' },
  'action.addPaymentMethod': { en: 'Add Payment Method', zh: '添加支付方式' },
  'action.signUpEvents':     { en: 'Sign Up for Events',  zh: '报名活动' },
  'action.backToPayments':   { en: 'Back to Payments',    zh: '返回账单' },
  'action.addFunds':            { en: 'Add Funds',             zh: '充值' },
  'action.withdraw':            { en: 'Withdraw',             zh: '提现' },
  'action.managePaymentMethods':{ en: 'Manage payment methods', zh: '管理支付方式' },
  'action.savePlan':            { en: 'Save plan',            zh: '保存' },
  // Flex monthly plan surface
  'flex.planTitle':      { en: 'Flex plan',      zh: '自选计划' },
  'flex.planningAhead':  { en: 'Planning ahead', zh: '规划' },
  'flex.planAheadDraft': { en: 'draft',          zh: '草稿' },
  'flex.pastMonths':     { en: 'Past months',      zh: '往期月份' },
  'flex.notAvailable':   { en: 'Not available this month', zh: '本月不可选' },
  'flex.askAssistant':   { en: 'Ask the assistant', zh: '问问 AI 助手' },
  'flex.clearAll':       { en: 'Clear all',          zh: '清空' },
  'flex.daysAttended':   { en: 'Days',               zh: '上课日' },
  'flex.thisMonth':      { en: 'this month',         zh: '本月' },
  'flex.thisMonthNote':  {
    en: 'These are the days already confirmed for this month. This month is locked and billed, so the calendar is view-only.',
    zh: '这是本月已确认的上课日期。本月计划已锁定并出账，日历仅供查看。',
  },
  'flex.nextMonth':      { en: 'next month',         zh: '下月' },
  'flex.nextMonthNote':  {
    en: 'These are the days confirmed for next month. The plan has locked, so the calendar is view-only; it is billed at the start of the month.',
    zh: '这是下月已确认的上课日期。计划已锁定，日历仅供查看；将于月初出账。',
  },
  'flexStatus.draft':    { en: 'draft',   zh: '草稿' },
  'flexStatus.locked':   { en: 'locked',  zh: '已锁定' },
  'flexStatus.billed':   { en: 'billed',  zh: '已出账' },
  'flexStatus.skipped':  { en: 'skipped', zh: '暂停' },
  // Section headers / field labels
  'header.activeEnrollments': { en: 'Active Enrollments',  zh: '当前报名' },
  'header.today':             { en: 'Today',               zh: '今天' },
  'header.pickup':            { en: 'Pickup',              zh: '接孩子' },
  'header.nextClass':         { en: 'Next class',          zh: '下节课' },
  'header.needsYou':          { en: 'Needs you',           zh: '待处理' },
  'header.enrolled':          { en: 'Enrolled',            zh: '当前报名' },
  'header.thisWeek':          { en: 'This week',           zh: '本周课表' },
  'header.enrollmentHistory': { en: 'Enrollment History',  zh: '报名历史' },
  'header.paymentHistory':    { en: 'Payment History',     zh: '付款记录' },
  'header.waiver':            { en: 'Waiver',              zh: '免责声明' },
  'header.emergencyContact':  { en: 'Emergency Contact',   zh: '紧急联系人' },
  'header.allergies':         { en: 'Allergies',           zh: '过敏' },
  'header.physician':         { en: 'Physician',           zh: '医生' },
  'header.familyAccess':      { en: 'Family Access',       zh: '家庭成员' },
  'header.paymentMethods':    { en: 'Your payment methods', zh: '支付方式' },
  'header.balanceRewards':    { en: 'BHCS balance & rewards', zh: '余额与奖励' },
  'header.quickActions':      { en: 'Quick Actions',       zh: '快捷操作' },
  'header.activePrograms':    { en: 'Active Programs',     zh: '当前课程' },
  'header.recommendedPrograms': { en: 'Recommended Programs', zh: '推荐课程' },
  'action.allPrograms':       { en: 'All Programs',        zh: '全部课程' },
  'header.totalBalance':      { en: 'Total balance',       zh: '账户余额' },
  'header.recentActivity':    { en: 'Recent activity',     zh: '近期记录' },
  'header.aboutProgram':      { en: 'About This Program',  zh: '课程介绍' },
  'header.pricing':           { en: 'Pricing',             zh: '收费' },
  'header.pricingDetails':    { en: 'Pricing Details',     zh: '收费明细' },
  'header.enrolledStudents':  { en: 'Enrolled Students',   zh: '已报名学生' },
  'header.selectWeeklySessions': { en: 'Select Weekly Sessions', zh: '选择每周课程' },
  'header.selectDays':        { en: 'Select Days to Attend', zh: '选择上课日期' },
  'header.programDetails':    { en: 'Program Details',     zh: '课程详情' },
  'header.lineItems':         { en: 'Line Items',          zh: '收费项目' },
  'header.enrollmentDetails': { en: 'Enrollment Details',  zh: '报名详情' },
  'header.withdrawFunds':     { en: 'Withdraw Funds',      zh: '提现' },
  'header.paymentPreferences':{ en: 'Payment preferences', zh: '支付偏好' },
  'header.accountInformation':{ en: 'Account Information',  zh: '账户信息' },
  'header.paymentSettings':   { en: 'Payment Settings',    zh: '支付设置' },
  'header.aboutPortal':       { en: 'About BHCS Portal',   zh: '关于 BHCS Portal' },
  'label.amountDue':          { en: 'Amount Due',          zh: '应付金额' },
  'label.balanceDue':         { en: 'Balance Due',         zh: '应付余额' },
  'label.dueNow':             { en: 'Due now',             zh: '待缴费用' },
  'label.nextClass':          { en: 'Next Class',          zh: '下节课' },
  'label.history':            { en: 'History',             zh: '历史' },
  'label.lunchIncluded':      { en: 'Lunch: Included',     zh: '午餐：含' },
  'label.lunchNotIncluded':   { en: 'Lunch: Not included', zh: '午餐：不含' },
  'label.fallbackPaymentMethod': { en: 'Fallback payment method', zh: '备用支付方式' },
  'label.noClassOn':          { en: 'No class on',          zh: '停课日期' },
  'label.programDayOff':      { en: 'Program day off',      zh: '本课程停课' },
  'label.session':            { en: 'session',              zh: '节课' },
  'label.sessions':           { en: 'sessions',             zh: '节课' },
  // Tabs (Payments page subtabs)
  'tab.invoices':         { en: 'Invoices',        zh: '账单' },
  'tab.cash':             { en: 'Cash',            zh: '余额' },
  // Footer
  'footer.privacyPolicy':   { en: 'Privacy Policy',   zh: '隐私政策' },
  'footer.termsOfService':  { en: 'Terms of Service', zh: '服务条款' },
  // Account menu
  'menu.accountSettings': { en: 'Account settings', zh: '账户设置' },
  'menu.signOut':         { en: 'Sign out',         zh: '退出' },
  'nav.faq':              { en: 'FAQ',              zh: '常见问题' },
  'nav.assistant':        { en: 'Assistant',        zh: '助手' },
  'nav.bhcsCash':         { en: 'BHCS Cash',        zh: '余额' },
  'nav.meetingSignup': { en: 'Meeting signup', zh: '家长会预约' },
  'nav.autoPay':          { en: 'Auto Pay',         zh: '自动缴费' },
  'genie.title':          { en: 'Hi! How can I help?', zh: '您好！有什么可以帮您？' },
  'genie.subtitle':       { en: 'Ask in English or Chinese.', zh: '可用中文或英文提问。' },
  'genie.placeholder':    { en: 'Ask a question…',   zh: '输入您的问题…' },
  'genie.newChat':        { en: 'New chat',          zh: '新对话' },
  'genie.thinking':       { en: 'Looking that up…',  zh: '正在查询…' },
  'genie.disclaimer':     { en: 'Answers are for reference only — if in doubt, verify with billing@bhcskids.com.', zh: '答案仅供参考，如有疑问，请联系 billing@bhcskids.com 核实。' },
  'genie.feedbackPrompt': { en: 'Helpful?',           zh: '有帮助吗？' },
  'genie.feedbackThanks': { en: 'Thanks!',            zh: '感谢反馈！' },
  'genie.stop':           { en: 'Stop',              zh: '停止' },
  'genie.retry':          { en: 'Retry',             zh: '重试' },
  'genie.cancelled':      { en: 'Cancelled.',        zh: '已取消。' },
  'genie.status.thinking': { en: 'Thinking…',            zh: '正在思考…' },
  'genie.status.querying': { en: 'Checking your records…', zh: '正在查询您的数据…' },
  'genie.status.digging':  { en: 'Digging deeper…',       zh: '正在进一步分析…' },
  'genie.close':          { en: 'Close',             zh: '关闭' },
  'genie.openFullPage':   { en: 'Open full page',    zh: '全屏打开' },
  // Starter questions. Each is SENT as the question, one language at a time: the assistant
  // answers in Chinese whenever the question has any Chinese in it, so a combined
  // "中文 English" chip answered English speakers in Chinese. Chosen from what parents
  // actually asked (app_events parent_agent_query, Jun–Sep 2026).
  'genie.chip.explainInvoices':  { en: 'Explain my latest invoice',                   zh: '解释我最新的账单' },
  'genie.chip.signUp':           { en: 'What camps and classes can I sign up for?',   zh: '最近有哪些营和课程可以报名？' },
  'genie.chip.flexDays':         { en: 'Which Flex days did I pick?',                 zh: '我选了哪几天 Flex？' },
  'genie.chip.nextClassWhere':   { en: "Where and when is my child's next class?",   zh: '孩子下节课在哪里、几点？' },
  // Kaleido's starter questions. Kaleido is English-only, so `zh` is never shown for them;
  // it is filled in because every label has one.
  'genie.chip.comingUp':         { en: 'What workshops and classes are coming up?', zh: '接下来有哪些工作坊和课程？' },
  'genie.chip.programsForAge':   { en: "Which programs fit my child's age?",        zh: '哪些课程适合我孩子的年龄？' },
  'genie.chip.nextClass':        { en: "When is my child's next class?",            zh: '孩子的下一节课是什么时候？' },
};
